// @flow

// Libs
import React from 'react'
import _isEqual from 'lodash/isEqual'

// Constants
import {
  PROP_ORDER,
  PROP_COUNTRY,
  SUPPLIES,
  PROP_IS_LOADING,
  PROP_USER_BILLING_INFO,
  PROP_ADDRESS,
  PROP_CITY,
  PROP_PROVINCE,
  PROP_POSTAL_CODE,
  PROP_SUPPLY_QUANTITY,
  PROP_SHIPPING_PACKAGE_TYPE,
  BOX,
} from 'hfty-model-orders'

// Components
import Header from '../Header'
import Footer from '../Footer'
import ShippingForm from '../ShippingForm'
import SoilSampleCard from '../ItemCards/SoilSample'
import { BillingPopUp } from 'hfty-components-confirm-billing'

// Style
import { styles as s } from './style'

export default class Layout extends React.Component {
  props: {
    operation: Object,
    supplies: Object,
    currentUser: Object,
    order: Object,
    actions: {
      createOrder: Function,
      updateUserBillingInfo: Function,
      onSubmitOperations: Function,
      onChangeOperation: Function,
      onCancelOperations: Function,
    },
  }

  state: {
    selectionSupplyMessage: string,
    billingPopUpIsOpen: boolean,
  }

  constructor(props: Object) {
    super(props)

    this.state = {
      selectionSupplyMessage: '',
      billingPopUpIsOpen: false,
    }
  }

  componentDidMount() {
    if (!this.props.operation) {
      this.runOrderOperation(this.props)
    }
  }

  renderOrderFlowInitialStage = (): React.Element<any> => {
    const { operation, supplies } = this.props

    return (
      <div style={s.mainWrapper}>
        <BillingPopUp
          isOpen={this.state.billingPopUpIsOpen}
          onCancel={(): Function => this.handleBillingPopUpActions('cancel')}
          onSubmit={(): Function => this.handleBillingPopUpActions('submit')}
          onUpdate={(payload: Object): Function => this.handleBillingPopUpActions('update', payload)}
          userBillingInfo={operation.model[PROP_USER_BILLING_INFO]}
          isBillingInfoChanged={this.checkIfUserBillingInfoWasChanged()}
        />

        <div style={s.main}>
          <div style={s.cardsList}>
            {Object.keys(supplies).length !== 0 ? this.renderCards() : <span>No supplies</span>}
          </div>

          <ShippingForm
            onFormFieldChange={(name: string, val: any): Function => this.handleUpdateOrderShipTo(name, val)}
            model={this.getOperationModel()}
          />
        </div>

        <div style={s.errorsContainer}>
          <span style={s.errorMessage}>
            {this.checkErrors()}
            {this.state.selectionSupplyMessage}
          </span>
        </div>

        <Footer
          isLoading={operation.model[PROP_IS_LOADING]}
          onCheckout={this.onSubmit}
          totalPrice={this.getTotalPrice()}
        />
      </div>
    )
  }

  handleRenderingStageProcess = (): React.Element<any> => {
    const { operation } = this.props

    if (operation) {
      return this.renderOrderFlowInitialStage()
    } else if (!operation && this.props[PROP_ORDER]) {
      this.runOrderOperation(this.props)
    } else {
      return <div>... Loading</div>
    }
  }

  render(): React.Element<any> {
    return (
      <div style={s.orderSuppliesLayout}>
        <div style={s.layoutContent}>
          <div style={s.layoutHeader}>
            <Header />
          </div>

          {this.handleRenderingStageProcess()}
        </div>
      </div>
    )
  }

  runOrderOperation = (lastProps: Object) => {
    if (lastProps.currentUser) {
      const { clientBillingAddress } = lastProps.currentUser

      lastProps.actions.createOrder({
        [PROP_ORDER]: {
          ...clientBillingAddress,
          [PROP_COUNTRY]: 'US',
          [SUPPLIES]: [],
          [PROP_SHIPPING_PACKAGE_TYPE]: BOX,
        },
      })
    }
  }

  checkErrors = (): string => {
    const error = this.props.operation.errors[0]
    let result

    if (error) {
      Object.getOwnPropertyNames(error).forEach((val: string) => {
        result = `${val}: ${error[val]}`
      })
    }
    return result
  }

  getTotalPrice = (): number => {
    if (this.props.operation.model && this.props.operation.model.order.supplies) {
      if (this.props.operation.model.order.supplies.length !== 0) {
        return this.props.operation.model.order.supplies
          .map((supply: Object): number => parseInt(supply.cost))
          .reduce((prev: number, current: number): number => prev + current)
      }
    }
  }

  getOperationModel = (): Object => {
    const { operation } = this.props
    return operation && operation.model
  }

  handleUpdateOrderShipTo = (fieldName: string, value: any) => {
    this.props.actions.onChangeOperation({
      ...this.props.operation,
      model: {
        [PROP_ORDER]: {
          ...this.props.operation.model[PROP_ORDER],
          [fieldName]: value,
        },
      },
    })
  }

  handleUpdateSupplies = (supplyData: Object) => {
    const { operation } = this.props

    function _onUpdateSupply(): Array<Object> {
      const { supplies } = operation.model.order
      const rest = supplies.find((supply: Object): Object => supply.supply !== supplyData.supply)
      const current = Object.assign({}, supplyData)

      if (supplyData.cost === 0) {
        return rest ? [rest] : []
      }

      return rest ? [rest, current] : [current]
    }

    this.props.actions.onChangeOperation({
      ...operation,
      model: {
        [PROP_ORDER]: {
          ...operation.model[PROP_ORDER],
          [SUPPLIES]: [..._onUpdateSupply()],
          [PROP_SUPPLY_QUANTITY]: supplyData ? supplyData.quantity : null,
        },
      },
    })
  }

  onCancel = () => {
    this.props.actions.onCancelOperations()
  }

  isPageValid = (): boolean => {
    if (this.props.operation.model.order.supplies.length > 0) {
      this.setState({
        selectionSupplyMessage: '',
      })
      return true
    } else {
      this.setState({
        selectionSupplyMessage: 'At least one supply should be selected',
      })
    }
  }

  askRememberBillingInfo = (): boolean => {
    return localStorage.getItem('rememberBillingInfo') ? !localStorage.getItem('rememberBillingInfo') === 'true' : true
  }

  onSubmit = () => {
    if (this.isPageValid()) {
      if (this.askRememberBillingInfo()) {
        this.setState({
          ...this.state,
          billingPopUpIsOpen: true,
        })
      } else {
        this.props.actions.onSubmitOperations()
      }
    }
  }

  checkIfUserBillingInfoWasChanged = (): boolean => {
    const {
      operation,
      currentUser: { clientBillingAddress },
    } = this.props
    const previousUserBillingInfo = {
      [PROP_ADDRESS]: clientBillingAddress[PROP_ADDRESS],
      [PROP_CITY]: clientBillingAddress[PROP_CITY],
      [PROP_COUNTRY]: clientBillingAddress[PROP_COUNTRY],
      [PROP_PROVINCE]: clientBillingAddress[PROP_PROVINCE],
      [PROP_POSTAL_CODE]: clientBillingAddress[PROP_POSTAL_CODE],
    }
    const currentUserBillingInfo = operation.model[PROP_USER_BILLING_INFO]

    return !_isEqual(previousUserBillingInfo, currentUserBillingInfo)
  }

  getReversedBillingModel = (): Object => {
    const {
      currentUser: { clientBillingAddress },
    } = this.props

    return {
      [PROP_ADDRESS]: clientBillingAddress[PROP_ADDRESS],
      [PROP_CITY]: clientBillingAddress[PROP_CITY],
      [PROP_COUNTRY]: clientBillingAddress[PROP_COUNTRY],
      [PROP_PROVINCE]: clientBillingAddress[PROP_PROVINCE],
      [PROP_POSTAL_CODE]: clientBillingAddress[PROP_POSTAL_CODE],
    }
  }

  handleBillingPopUpActions = (actionType: string, payload: Object) => {
    const { operation, actions } = this.props

    switch (actionType) {
      case 'update':
        this.props.actions.onChangeOperation({
          ...operation,
          model: {
            ...operation.model,
            [PROP_USER_BILLING_INFO]: {
              ...operation.model[PROP_USER_BILLING_INFO],
              [payload.field]: payload.value,
            },
          },
        })
        break

      case 'submit':
        const isBillingInfoChanged = this.checkIfUserBillingInfoWasChanged()

        if (!isBillingInfoChanged) {
          this.setState(
            {
              ...this.state,
              billingPopUpIsOpen: false,
            },
            () => {
              actions.onSubmitOperations()
            }
          )
        } else {
          this.setState(
            {
              ...this.state,
              billingPopUpIsOpen: false,
            },
            () => {
              actions.updateUserBillingInfo(operation.model[PROP_USER_BILLING_INFO])
            }
          )
        }
        break

      case 'cancel':
        this.setState(
          {
            ...this.state,
            billingPopUpIsOpen: false,
          },
          () => {
            this.props.actions.onChangeOperation({
              ...operation,
              model: {
                ...operation.model,
                [PROP_USER_BILLING_INFO]: {
                  ...this.getReversedBillingModel(),
                },
              },
            })
          }
        )
    }
  }

  renderCards = (): React.Element<any> => {
    // TODO pass to the cards info from model (already selected supplies)
    // TODO __BUG__ -> on location changed model has selected supplies but UI cards no

    const suppliesValues = Object.values(this.props.supplies)
    const soilSampleData = suppliesValues.find((card: Object): Array => card.name === 'Soil Sample Supplies Pack')

    return (
      <div style={s.cardsWrapper}>
        <SoilSampleCard
          onUpdateCardTotalCost={(data: Object): Function => this.handleUpdateSupplies(data)}
          data={soilSampleData}
        />
      </div>
    )
  }
}
